import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
} from '@material-ui/core';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useHistory, useParams } from 'react-router-dom';
import PageTitle from '../../ui/PageTitle';
import { useButtonsStyles } from '../../../styles/useButtonsStyles';
import { useYardiSyncRunDetailQuery } from '../../../queries/yardiSync';
import { YardiSyncRunPropertyResult } from '../../../types/yardiSync';
import { formatDateTimeToLocal } from '../../../formatters/dateFormatters/dateFormatters';
import { PRINT_HYPHEN } from '../../../constants';

const isTerminalStatus = (status?: string): boolean => {
  if (!status) {
    return false;
  }
  return ['completed', 'partial', 'failed'].includes(status);
};

const statusStyles = (status?: string) => {
  switch (status) {
    case 'completed':
      return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    case 'partial':
      return { backgroundColor: '#fff3e0', color: '#ef6c00' };
    case 'failed':
      return { backgroundColor: '#ffebee', color: '#c62828' };
    case 'running':
      return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    case 'queued':
      return { backgroundColor: '#f3e5f5', color: '#6a1b9a' };
    default:
      return { backgroundColor: '#eeeeee', color: '#424242' };
  }
};

const renderStatusPill = (status?: string) => (
  <Box
    component="span"
    style={{
      borderRadius: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 600,
      padding: '4px 10px',
      textTransform: 'capitalize',
      ...statusStyles(status),
    }}
  >
    {status?.replace(/_/g, ' ') || 'unknown'}
  </Box>
);

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }
  if (responseData && typeof responseData === 'object') {
    const data = responseData as { error?: string; message?: string };
    if (data.error) {
      return data.error;
    }
    if (data.message) {
      return data.message;
    }
  }
  const message = (error as Error)?.message;
  return message || fallback;
};

const formatSyncType = (syncType?: string | null): string => (
  syncType ? syncType.replace(/_/g, ' ') : PRINT_HYPHEN
);

const formatMetric = (value?: number | null): string | number => (
  value === null || value === undefined ? PRINT_HYPHEN : value
);

const YardiSyncRunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);
  const history = useHistory();
  const buttonClasses = useButtonsStyles();

  const {
    data: runDetail,
    isLoading,
    isFetching,
    isError,
    error,
  } = useYardiSyncRunDetailQuery(runId, {
    refetchInterval: (data) => (isTerminalStatus(data?.status) ? false : 5000),
  });

  const columns = useMemo<MRT_ColumnDef<YardiSyncRunPropertyResult>[]>(() => [
    {
      header: 'Property Code',
      accessorKey: 'propertyCode',
      enableSorting: false,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => renderStatusPill(row.status),
      enableSorting: false,
    },
    {
      header: 'Building ID',
      accessorKey: 'buildingId',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.buildingId),
      enableSorting: false,
      size: 90,
    },
    {
      header: 'Building Code',
      accessorKey: 'buildingCode',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => row.buildingCode || PRINT_HYPHEN,
      enableSorting: false,
      size: 110,
    },
    {
      header: 'Buildings',
      accessorKey: 'buildingsCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.buildingsCount),
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Floors',
      accessorKey: 'floorsCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.floorsCount),
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Spaces',
      accessorKey: 'spacesCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.spacesCount),
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Leases',
      accessorKey: 'leasesCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.leasesCount),
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Exceptions',
      accessorKey: 'exceptionsCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.exceptionsCount),
      enableSorting: false,
      size: 100,
    },
    {
      header: 'Documents',
      accessorKey: 'documentsCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.documentsCount),
      enableSorting: false,
      size: 90,
    },
    {
      header: 'Created',
      accessorKey: 'documentsCreatedCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.documentsCreatedCount),
      enableSorting: false,
      size: 90,
    },
    {
      header: 'Updated',
      accessorKey: 'documentsUpdatedCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.documentsUpdatedCount),
      enableSorting: false,
      size: 90,
    },
    {
      header: 'Deleted',
      accessorKey: 'documentsDeletedCount',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatMetric(row.documentsDeletedCount),
      enableSorting: false,
      size: 90,
    },
    {
      header: 'Error Message',
      accessorKey: 'errorMessage',
      enableSorting: false,
      size: 200,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (
        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              history.push({
                pathname: `/yardi/runs/${runId}/exceptions`,
                search: `?propertyCode=${row.propertyCode}`,
              });
            }}
          >
            View Exceptions
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              const search = new URLSearchParams();
              if (row.propertyCode) {
                search.set('propertyCode', row.propertyCode);
              }
              if (row.buildingCode) {
                search.set('buildingCode', row.buildingCode);
              }
              history.push({
                pathname: `/yardi/runs/${runId}/audit-events`,
                search: `?${search.toString()}`,
              });
            }}
          >
            View Audit Events
          </Button>
        </Box>
      ),
      enableSorting: false,
      size: 240,
    },
  ], [history, runId]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <PageTitle>{`Run ${runId}`}</PageTitle>
      </Box>
      {isError ? (
        <Typography color="error">
          {getErrorMessage(error, 'Unable to load run details.')}
        </Typography>
      ) : (
        <>
          <Box mb={2}>
            <Typography variant="body2">
              {'Status: '}
              {renderStatusPill(runDetail?.status)}
            </Typography>
            <Typography variant="body2">
              {`Sync Type: ${formatSyncType(runDetail?.syncType)}`}
            </Typography>
            <Typography variant="body2">
              {`Started: ${runDetail?.startedAt ? formatDateTimeToLocal(runDetail.startedAt) : PRINT_HYPHEN}`}
            </Typography>
            <Typography variant="body2">
              {`Finished: ${runDetail?.finishedAt ? formatDateTimeToLocal(runDetail.finishedAt) : PRINT_HYPHEN}`}
            </Typography>
            <Typography variant="body2">
              {`Notes: ${runDetail?.notes || PRINT_HYPHEN}`}
            </Typography>
          </Box>
          <MaterialReactTable
            columns={columns}
            data={runDetail?.properties || []}
            enableTopToolbar={false}
            enableColumnResizing
            enableColumnActions={false}
            enableFullScreenToggle={false}
            enableRowOrdering={false}
            enableSorting={false}
            enableGlobalFilterModes={false}
            enableDensityToggle={false}
            state={{ isLoading: isLoading || isFetching }}
            layoutMode="grid"
            muiTableProps={{
              sx: {
                tableLayout: 'fixed',
                display: 'table',
              },
            }}
          />
        </>
      )}
      <Box mt={2}>
        <Button
          className={buttonClasses.standard}
          variant="outlined"
          onClick={() => history.push('/yardi')}
        >
          Back to Dashboard
        </Button>
      </Box>
    </>
  );
};

export default YardiSyncRunDetail;
